<template>
  <div :class="{ 'line-chart-container': !!$slots.empty }">
    <v-chart
      :id="`${props.id}-line-chart`"
      ref="chartRef"
      :data-testid="sectionTag"
      class="line-chart"
      :option="option"
      :init-options="initOptions"
      autoresize
      @globalout="handleMouseOut"
    >
    </v-chart>
    <div :data-testid="sectionTag ? `${sectionTag}-empty-state` : undefined" class="empty-state-slot">
      <slot name="empty"></slot>
    </div>
    <Teleport v-if="popoverVisible" to="body">
      <div
        class="w-line-chart-popover-host"
        :style="
          chartPopoverHostStyle(popoverX, popoverY, popoverPlacement, CHART_POPOVER_POINT_GAP, { animated: true })
        "
      >
        <WChartPopover v-bind="popoverContent" :section-tag="`${sectionTag}-popover`" />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { LegendComponent, GridComponent, MarkLineComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { useChartColors } from '@/composables/useChartColors'
import { useMath } from '@/composables/useMath'
import i18n, { $n } from '@/plugins/i18n'
import { tokens } from '@/plugins/vuetify'
import { AxisStrategy } from '@/enums'
import WChartPopover from './WChartPopover.vue'
import {
  CHART_POPOVER_POINT_GAP,
  chartPopoverHostStyle,
  chooseChartPopoverPlacement,
  resolveChartPopoverProps,
  type CartesianHoverContext,
  type CartesianSeriesPoint,
  type WChartPopoverConfig,
  type ChartPopoverPlacement
} from './chart-popover-helpers'

use([LegendComponent, GridComponent, LineChart, CanvasRenderer, MarkLineComponent, TooltipComponent])

export type WLineChartPopoverConfig = WChartPopoverConfig<CartesianHoverContext>
export type { CartesianHoverContext }

type LineChartSeriesValue = number | string | null | undefined
type LineChartValue = LineChartSeriesValue | { value?: LineChartSeriesValue }

interface LineTooltipParam {
  seriesType?: string
  dataIndex?: number
  seriesIndex?: number
  value?: LineChartValue
  color?: string
  name?: string
  seriesName?: string
}

export interface WLineChartData {
  labels?: Array<string[] | undefined>
  seriesNames?: string[]
  values?: Array<LineChartSeriesValue[] | undefined>
  symbol?: string[]
  colorOverride?: Array<string | null | undefined>
  lineStyleType?: string[]
}

export interface WLineChartProps {
  sectionTag?: string
  id: string
  chartData: WLineChartData
  contentVariant?: string
  /** Abbreviates thousands and larger values with locale-aware compact notation. */
  suffix?: boolean
  /** Adds comma grouping to displayed numeric values (for example, 42,200). */
  comma?: boolean
  hasMarkline?: boolean
  markLineType?: string
  marklineValue?: number | null
  minOffset?: number
  horizontalPadding?: number
  chartHeight?: number
  yAxisStrategy?: AxisStrategy
  smooth?: boolean | number
  showSymbol?: boolean
  popover?: WLineChartPopoverConfig
}

const props = withDefaults(defineProps<WLineChartProps>(), {
  sectionTag: 'unspecified',
  contentVariant: '',
  suffix: false,
  comma: false,
  hasMarkline: false,
  markLineType: 'predefined',
  marklineValue: null,
  minOffset: 10,
  horizontalPadding: 0,
  chartHeight: 400,
  yAxisStrategy: AxisStrategy.Default,
  smooth: false,
  showSymbol: true,
  popover: undefined
})

const initOptions = { devicePixelRatio: window.devicePixelRatio }

const { getColors } = useChartColors()
const { roundToFixed } = useMath()

const chartRef = ref<InstanceType<typeof VChart>>()
const popoverVisible = ref(false)
const popoverX = ref(0)
const popoverY = ref(0)
const popoverPlacement = ref<ChartPopoverPlacement>('top')
const popoverContent = ref<Partial<ReturnType<typeof resolveChartPopoverProps>>>({})
const LINE_CHART_POPOVER_TRANSITION_MS = 140
const LINE_CHART_POPOVER_TRANSITION_DURATION = `${LINE_CHART_POPOVER_TRANSITION_MS}ms`

const hPad = computed(() => {
  return Number(props.horizontalPadding)
})
const gridLeft = computed(() => 65 + hPad.value)
const gridRight = computed(() => 50 + hPad.value)

const getChartNumericValue = (value?: LineChartValue): number | undefined => {
  const rawValue = typeof value === 'object' && value !== null ? value.value : value
  if (rawValue === null || rawValue === undefined) return undefined

  const numericValue = Number(rawValue)
  return Number.isNaN(numericValue) ? undefined : numericValue
}

const hasChartValue = (value: LineChartSeriesValue): value is number | string => {
  return value !== null && value !== undefined
}

const isTransparentColor = (color?: string): boolean => {
  const normalizedColor = color?.replace(/\s+/g, '').toLowerCase()
  if (!normalizedColor) return false
  const isTransparentHex =
    (/^#[0-9a-f]{8}$/.test(normalizedColor) && normalizedColor.endsWith('00')) ||
    (/^#[0-9a-f]{4}$/.test(normalizedColor) && normalizedColor.endsWith('0'))

  return (
    normalizedColor === 'transparent' ||
    /^rgba\([^,]+,[^,]+,[^,]+,0(?:\.0+)?\)$/.test(normalizedColor) ||
    /^hsla\([^,]+,[^,]+,[^,]+,0(?:\.0+)?\)$/.test(normalizedColor) ||
    isTransparentHex
  )
}

interface NumericDisplayOptions {
  contentVariant: string
  suffix: boolean
  comma: boolean
  locale: string
}

const numericDisplayOptions = computed<NumericDisplayOptions>(() => ({
  contentVariant: props.contentVariant,
  suffix: props.suffix,
  comma: props.comma,
  locale: i18n.global.locale.value
}))
const hasNumericDisplayFormatting = computed(() => props.suffix || props.comma)

const formatNumber = (
  value: number,
  useGrouping: boolean,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2
): string => {
  return $n(value, {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
    signDisplay: value <= 0 ? 'exceptZero' : 'auto'
  })
}

const formatCompactNumber = (
  value: number,
  useGrouping: boolean,
  locale: string,
  minimumFractionDigits = 0,
  maximumFractionDigits = 2
): string => {
  if (Math.abs(value) < 1_000) {
    return formatNumber(value, useGrouping, minimumFractionDigits, maximumFractionDigits)
  }

  return $n(
    value,
    {
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
      useGrouping,
      signDisplay: value <= 0 ? 'exceptZero' : 'auto'
    },
    locale
  )
}

const formatNumericDisplay = (
  value: number,
  options: NumericDisplayOptions = numericDisplayOptions.value
): string | number => {
  if (options.contentVariant === 'percent') {
    const numericValue = options.suffix
      ? formatCompactNumber(value, options.comma, options.locale, 0, 0)
      : options.comma
        ? formatNumber(value, true, 0, 0)
        : roundToFixed(value, 0)
    return `${numericValue}%`
  }

  if (options.contentVariant === 'rating') {
    return options.suffix
      ? formatCompactNumber(value, options.comma, options.locale, 2, 2)
      : options.comma
        ? formatNumber(value, true, 2, 2)
        : roundToFixed(value, 2)
  }

  if (options.suffix) return formatCompactNumber(value, options.comma, options.locale)
  if (options.comma) return formatNumber(value, true)
  return roundToFixed(value, 0)
}

const formatLabel = (
  d: LineChartValue,
  options: NumericDisplayOptions = numericDisplayOptions.value
): string | number => {
  const valueToFormat = getChartNumericValue(d)
  if (valueToFormat === undefined) return ''
  return formatNumericDisplay(valueToFormat, options)
}

const axisLabelFormatter = computed(() => {
  const options = numericDisplayOptions.value
  return (value: LineChartValue) => formatLabel(value, options)
})

const chartColors = getColors(props.chartData.values?.length ?? 0)
const getSeries = computed(() => {
  if (
    !props.chartData.seriesNames ||
    !props.chartData ||
    !props.chartData.values ||
    props.chartData.values.length === 0
  ) {
    return []
  }
  let series = props.chartData.values.map((seriesValues = [], index: number) => {
    return {
      name: props.chartData.seriesNames?.[index] ?? '',
      data: seriesValues.map((value: LineChartSeriesValue) => {
        return {
          value: value ?? null
        }
      }),
      type: 'line',
      symbol: props.showSymbol ? props.chartData.symbol?.[index] || 'emptyCircle' : 'circle',
      smooth: props.smooth,
      connectNulls: true,
      borderColor: props.chartData.colorOverride?.[index] ?? chartColors[index],
      lineStyle: {
        color: props.chartData.colorOverride?.[index] ?? chartColors[index],
        type: props.chartData.lineStyleType?.[index] ?? 'solid'
      },
      itemStyle: props.showSymbol
        ? { color: props.chartData.colorOverride?.[index] ?? chartColors[index] }
        : { color: 'transparent', borderColor: 'transparent' },
      emphasis: {
        itemStyle: {
          color: props.chartData.colorOverride?.[index] ?? chartColors[index],
          borderColor: props.chartData.colorOverride?.[index] ?? chartColors[index]
        }
      },
      markLine: getMarkLineOption(seriesValues)
    }
  })
  return series
})

const getMarkLineOption = (series: LineChartSeriesValue[]) => {
  if (!props.hasMarkline) return {}
  if (props.markLineType === 'trajectory') {
    return {
      silent: true,
      data: [
        [
          {
            symbol: 'none',
            xAxis: getFirstValidData(series).index,
            yAxis: getFirstValidData(series).value,
            lineStyle: {
              type: 'solid',
              opacity: 0.25
            }
          },
          {
            symbol: 'none',
            xAxis: getLastValidData(series).index,
            yAxis: getLastValidData(series).value
          }
        ]
      ]
    }
  }
  return {
    symbol: 'none',
    lineStyle: {
      type: 'dashed'
    },
    data: [
      {
        yAxis: props.marklineValue ?? getMinMaxValue.value.max,
        label: {
          show: true,
          position: 'end',
          formatter: hasNumericDisplayFormatting.value
            ? formatNumericDisplay(props.marklineValue ?? getMinMaxValue.value.max)
            : (props.marklineValue ?? getMinMaxValue.value.max)
        }
      }
    ]
  }
}

const getFirstValidData = (series: LineChartSeriesValue[]) => {
  let firstValidData = series.find(hasChartValue)
  return {
    index: firstValidData !== undefined ? series.indexOf(firstValidData) : 0,
    value: firstValidData
  }
}

const getLastValidData = (series: LineChartSeriesValue[]) => {
  let reverseArray = [...series]
  let lastValidData = reverseArray.reverse().find(hasChartValue)
  return {
    index: lastValidData !== undefined ? series.lastIndexOf(lastValidData) : series.length - 1,
    value: lastValidData
  }
}

const getMinMaxValue = computed(() => {
  if (!props.chartData?.values) {
    return { max: 100, min: 0 }
  }
  let max = 0
  let min = 999999999999
  let hasValidValue = false
  props.chartData.values.forEach((element = []) => {
    let filteredArray = element
      .map((item) => getChartNumericValue(item))
      .filter((item): item is number => item !== undefined)
    if (filteredArray.length === 0) return

    hasValidValue = true
    let maxValue = Math.max(...filteredArray)
    let minValue = Math.min(...filteredArray)
    if (maxValue > max) {
      max = maxValue
    }
    if (minValue < min) {
      min = minValue
    }
  })
  if (!hasValidValue) {
    return { max: 100, min: 0 }
  }
  if (props.yAxisStrategy == AxisStrategy.Compact) {
    max += 0.1
    min -= 0.1
    return { max, min }
  } else {
    if (min == 999999999999) {
      min = 0
    }
    if (max == 0) {
      max = 100
    }
    if (min == 0) {
      min = 0
    } else {
      let interval = Math.ceil((max - min) / 5)
      min = max - 5 * interval
    }
    return { max, min }
  }
})

const getLegendData = computed(() => {
  let legendData = props.chartData?.seriesNames?.map((seriesName: string, index: number) => {
    return {
      name: seriesName,
      icon:
        props.chartData.lineStyleType?.[index] == 'dashed'
          ? 'path://M180 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40z, M810 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40zm, M1440 1000 l0 -40 200 0 200 0 0 40 0 40 -200 0 -200 0 0 -40z'
          : 'rect',
      itemStyle: {
        color: props.chartData.colorOverride?.[index] ?? chartColors[index]
      },
      lineStyle: {
        color: props.chartData.colorOverride?.[index] ?? chartColors[index]
      }
    }
  })
  return legendData
})

const calculateYAxisInterval = () => {
  if (props.yAxisStrategy == AxisStrategy.Compact) {
    return (getMinMaxValue.value.max - getMinMaxValue.value.min) / 5
  } else {
    return Math.round((getMinMaxValue.value.max - getMinMaxValue.value.min) / 5)
  }
}

const option = computed(() => {
  const firstData = props.chartData.labels?.[0] ?? []

  return {
    grid: {
      containLabel: true,
      top: 35,
      left: gridLeft.value,
      right: gridRight.value,
      bottom: 54
    },
    legend: {
      data: getLegendData.value,
      itemGap: 14,
      bottom: 0,
      left: 0
    },
    tooltip: {
      trigger: 'axis',
      triggerOn: 'mousemove',
      backgroundColor: 'transparent',
      borderWidth: 0,
      padding: 0,
      extraCssText: 'box-shadow: none; pointer-events: none;',
      formatter(params: LineTooltipParam | LineTooltipParam[]) {
        updatePopoverContent(params)
        return ''
      },
      position(point: number[], params: LineTooltipParam | LineTooltipParam[]) {
        return updatePopoverPosition(params) ?? point
      },
      axisPointer: {
        type: 'line',
        snap: true,
        animation: true,
        animationDurationUpdate: LINE_CHART_POPOVER_TRANSITION_MS,
        animationEasingUpdate: 'cubicOut',
        lineStyle: {
          color: tokens.color.border.default,
          width: 1,
          type: 'solid'
        },
        label: {
          show: false
        }
      }
    },
    xAxis: {
      type: 'category',
      data: firstData,
      boundaryGap: false,
      axisTick: {
        show: true
      },
      axisLabel: {
        margin: firstData?.length < 12 ? 10 : 12,
        rotate: firstData?.length < 12 ? 0 : 45,
        color: tokens.color.content.text.primary
      },
      axisLine: {
        lineStyle: {
          color: tokens.color.border.subtle
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: axisLabelFormatter.value,
        color: tokens.color.content.text.primary
      },
      min: getMinMaxValue.value.min,
      max: getMinMaxValue.value.max,
      interval: calculateYAxisInterval(),
      offset: 55,
      axisLine: {
        show: true,
        lineStyle: {
          color: tokens.color.border.subtle
        }
      },
      axisTick: {
        show: true
      },
      splitLine: {
        show: false
      }
    },
    series: getSeries.value
  }
})

const buildSeriesSnapshot = (dataIndex: number): CartesianSeriesPoint[] => {
  const seriesNames = props.chartData.seriesNames ?? []
  const values = props.chartData.values ?? []
  return values.flatMap((seriesValues = [], seriesIdx) => {
    const value = seriesValues[dataIndex]
    if (!hasChartValue(value)) return []

    return [
      {
        seriesIndex: seriesIdx,
        name: seriesNames[seriesIdx] ?? '',
        value,
        color: props.chartData.colorOverride?.[seriesIdx] ?? chartColors[seriesIdx]
      }
    ]
  })
}

const getTooltipParams = (params: LineTooltipParam | LineTooltipParam[]): LineTooltipParam[] => {
  return Array.isArray(params) ? params : [params]
}

const getTooltipValue = (param?: LineTooltipParam): number | undefined => {
  return getChartNumericValue(param?.value)
}

const getPrimaryTooltipParam = (params: LineTooltipParam[]): LineTooltipParam | undefined => {
  return (
    params.find((param) => param?.seriesType === 'line' && getTooltipValue(param) !== undefined) ??
    params.find((param) => param?.dataIndex !== undefined && getTooltipValue(param) !== undefined)
  )
}

const hidePopover = () => {
  popoverVisible.value = false
  popoverContent.value = {}
}

const formatPopoverValue = (value: string | number): string | number => {
  if (typeof value === 'string' && value.trim() === '') return value

  const numericValue = getChartNumericValue(value)
  return numericValue === undefined ? value : formatNumericDisplay(numericValue)
}

const formatResolvedPopoverContent = (
  content: ReturnType<typeof resolveChartPopoverProps>
): ReturnType<typeof resolveChartPopoverProps> => {
  if (!hasNumericDisplayFormatting.value) return content

  return {
    ...content,
    value: content.value === undefined ? undefined : formatPopoverValue(content.value),
    metrics: content.metrics?.map((metric) => ({
      ...metric,
      value: formatPopoverValue(metric.value)
    }))
  }
}

const updatePopoverContent = (params: LineTooltipParam | LineTooltipParam[]) => {
  if (!props.popover || props.popover.enabled === false) {
    hidePopover()
    return
  }

  const tooltipParams = getTooltipParams(params)
  const primaryParam = getPrimaryTooltipParam(tooltipParams)
  if (!primaryParam) {
    hidePopover()
    return
  }

  const dataIndex = primaryParam.dataIndex
  if (dataIndex === undefined) {
    hidePopover()
    return
  }
  const value = getTooltipValue(primaryParam)
  if (value === undefined) {
    hidePopover()
    return
  }

  const seriesIndex = primaryParam.seriesIndex ?? 0
  const labels = props.chartData.labels?.[0] ?? []
  const series = buildSeriesSnapshot(dataIndex)
  const primarySeries = series.find((seriesPoint) => seriesPoint.seriesIndex === seriesIndex)
  const color =
    (isTransparentColor(primaryParam.color) ? undefined : primaryParam.color) ??
    primarySeries?.color ??
    props.chartData.colorOverride?.[seriesIndex] ??
    chartColors[seriesIndex]

  const ctx: CartesianHoverContext = {
    dataIndex,
    seriesIndex,
    value,
    color,
    label: labels[dataIndex] ?? String(primaryParam.name ?? ''),
    seriesName: primaryParam.seriesName ?? primarySeries?.name ?? '',
    series
  }

  popoverContent.value = formatResolvedPopoverContent(resolveChartPopoverProps(props.popover, ctx))
  popoverVisible.value = true
}

const getFallbackLocalXPixel = (param: LineTooltipParam, chartRect: DOMRect): number => {
  const labels = props.chartData.labels?.[0] ?? []
  const left = gridLeft.value
  const right = gridRight.value
  const plotWidth = Math.max(chartRect.width - left - right, 0)
  if (labels.length <= 1) {
    return left + plotWidth / 2
  }
  return left + (plotWidth * Number(param.dataIndex ?? 0)) / (labels.length - 1)
}

const getFallbackLocalYPixel = (param: LineTooltipParam, chartRect: DOMRect): number => {
  const plotTop = Number(option.value.grid.top ?? 0)
  const plotBottom = chartRect.height - Number(option.value.grid.bottom ?? 0)
  const plotHeight = Math.max(plotBottom - plotTop, 0)
  const value = getTooltipValue(param)
  const yMin = getMinMaxValue.value.min
  const yMax = getMinMaxValue.value.max
  const yRange = yMax - yMin

  if (value === undefined || plotHeight === 0 || !Number.isFinite(value) || yRange <= 0) {
    return plotBottom
  }

  const yRatio = Math.min(Math.max((value - yMin) / yRange, 0), 1)
  return plotBottom - yRatio * plotHeight
}

const getLocalSeriesPixel = (param: LineTooltipParam, chartRect: DOMRect): number[] => {
  const fallbackX = getFallbackLocalXPixel(param, chartRect)
  const fallbackY = getFallbackLocalYPixel(param, chartRect)
  const value = getTooltipValue(param)
  if (value === undefined || param.dataIndex === undefined) return [fallbackX, fallbackY]

  const pixelPosition =
    typeof chartRef.value?.convertToPixel === 'function'
      ? chartRef.value.convertToPixel({ seriesIndex: param.seriesIndex ?? 0 }, [param.dataIndex, value])
      : undefined

  if (Array.isArray(pixelPosition) && typeof pixelPosition[0] === 'number') {
    return [
      Number.isFinite(pixelPosition[0]) ? pixelPosition[0] : fallbackX,
      Number.isFinite(pixelPosition[1]) ? pixelPosition[1] : fallbackY
    ]
  }
  if (typeof pixelPosition === 'number') {
    return [pixelPosition, fallbackY]
  }

  return [fallbackX, fallbackY]
}

const getEstimatedPopoverHeight = (): number => {
  if (props.popover?.variant === 'compact') {
    return 56
  }
  const rowCount = Math.max(props.chartData.values?.length ?? 1, 1)
  return 56 + rowCount * 28
}

const getPopoverPlacement = (localY: number, chartRect: DOMRect): ChartPopoverPlacement => {
  const plotTop = Number(option.value.grid.top ?? 0)
  const plotBottom = chartRect.height - Number(option.value.grid.bottom ?? 0)
  const availableAbove = localY - plotTop
  const availableBelow = plotBottom - localY
  const requiredSpace = getEstimatedPopoverHeight() + CHART_POPOVER_POINT_GAP

  return chooseChartPopoverPlacement(availableAbove, availableBelow, requiredSpace)
}

const updatePopoverPosition = (params: LineTooltipParam | LineTooltipParam[]): number[] | undefined => {
  const tooltipParams = getTooltipParams(params)
  const primaryParam = getPrimaryTooltipParam(tooltipParams)
  if (!primaryParam || primaryParam.dataIndex === undefined) return undefined

  const chartElement = document.getElementById(`${props.id}-line-chart`)
  const chartRect = chartElement?.getBoundingClientRect()
  if (!chartRect) return undefined

  const [localX, localY] = getLocalSeriesPixel(primaryParam, chartRect)

  popoverX.value = chartRect.left + localX
  popoverY.value = chartRect.top + localY
  popoverPlacement.value = getPopoverPlacement(localY, chartRect)

  return [localX, localY]
}

const handleMouseOut = () => {
  hidePopover()
}

const handleScroll = () => {
  hidePopover()
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, true)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true)
})
</script>

<style scoped>
.line-chart {
  height: v-bind(chartHeight + 'px');
}

.w-line-chart-popover-host {
  transition: transform v-bind(LINE_CHART_POPOVER_TRANSITION_DURATION) cubic-bezier(0.2, 0, 0.2, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.line-chart-container {
  position: relative;

  .empty-state-slot {
    position: absolute;
    top: calc(50% - 30px);
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
